import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaTimes, FaPaperPlane, FaUser, FaEnvelope, FaComment, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import apiService from '../services/api';
import { createFormToken, validateFormTime, isRandomString, requiresWordStructure } from '../utils/botDetection';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const formTokenRef = useRef(createFormToken());

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Reset form when modal closes; refresh token when it opens
  useEffect(() => {
    if (isOpen) {
      formTokenRef.current = createFormToken();
    } else {
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setHoneypot('');
        setErrors({});
        setStatus({ type: '', message: '' });
        setTouched({});
      }, 300);
    }
  }, [isOpen]);

  // Form validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Name is required';
        } else if (value.trim().length < 2) {
          return 'Name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          return 'Name must not exceed 50 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          return 'Name can only contain letters and spaces';
        }
        return '';

      case 'email':
        if (!value.trim()) {
          return 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        } else if (value.length > 100) {
          return 'Email must not exceed 100 characters';
        }
        return '';

      case 'message':
        if (!value.trim()) {
          return 'Message is required';
        } else if (value.trim().length < 10) {
          return 'Message must be at least 10 characters';
        } else if (value.trim().length > 1000) {
          return 'Message must not exceed 1000 characters';
        } else if (isRandomString(value)) {
          return 'Message appears invalid. Please describe your inquiry in plain English.';
        } else if (!requiresWordStructure(value)) {
          return 'Please describe your message using multiple words.';
        }
        return '';

      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time validation for touched fields
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot: silently succeed to confuse bots without revealing detection
    if (honeypot) {
      setStatus({ type: 'success', message: "Message sent successfully! We'll get back to you soon." });
      setFormData({ name: '', email: '', message: '' });
      setTouched({});
      setTimeout(() => handleClose(), 2000);
      return;
    }

    // Timing: reject submissions that arrive impossibly fast
    if (!validateFormTime(formTokenRef.current, 4000)) {
      setStatus({ type: 'error', message: 'Please take a moment to review your information before submitting.' });
      return;
    }

    // Mark all fields as touched
    setTouched({ name: true, email: true, message: true });

    // Validate form
    if (!validateForm()) {
      setStatus({
        type: 'error',
        message: 'Please fix the errors before submitting',
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const data = await apiService.post('/api/contact', {
        ...formData,
        _ft: formTokenRef.current,
        website: honeypot,
      }, { includeAuth: false });

      setStatus({
        type: 'success',
        message: data.message || 'Message sent successfully! We\'ll get back to you soon.'
      });
      setFormData({ name: '', email: '', message: '' });
      setTouched({});
      setErrors({});

      // Close modal after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to send message. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-wrapper fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div
        className="contact-modal-backdrop absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Modal Container */}
      <div className="contact-modal-container relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
        {/* Header */}
        <div className="contact-modal-header flex-shrink-0 bg-gradient-to-r from-primary to-teal dark:from-teal dark:to-primary p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl text-white">
                <FaPaperPlane className="text-3xl" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  Get in Touch
                </h2>
                <p className="text-white/90 text-sm mt-1">
                  We'd love to hear from you
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="contact-modal-close p-2 hover:bg-white/20 rounded-lg transition-all duration-300 text-white group disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close modal"
            >
              <FaTimes className="text-2xl group-hover:rotate-90 transition-transform duration-300" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="contact-modal-content flex-1 overflow-y-auto p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Honeypot — hidden from real users; bots fill it in */}
            <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Name Field */}
            <div>
              <label htmlFor="modal-name" className="block text-gray-900 dark:text-white font-semibold mb-2 flex items-center gap-2">
                <FaUser className="text-primary dark:text-teal" />
                Name *
              </label>
              <input
                type="text"
                id="modal-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.name && touched.name
                    ? 'border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900'
                    : 'border-gray-300 dark:border-gray-600 focus:border-primary dark:focus:border-teal focus:ring-2 focus:ring-primary/20 dark:focus:ring-teal/20'
                }`}
                placeholder="Your full name"
              />
              {errors.name && touched.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 animate-fade-in">
                  <FaExclamationCircle />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="modal-email" className="block text-gray-900 dark:text-white font-semibold mb-2 flex items-center gap-2">
                <FaEnvelope className="text-primary dark:text-teal" />
                Email *
              </label>
              <input
                type="email"
                id="modal-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.email && touched.email
                    ? 'border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900'
                    : 'border-gray-300 dark:border-gray-600 focus:border-primary dark:focus:border-teal focus:ring-2 focus:ring-primary/20 dark:focus:ring-teal/20'
                }`}
                placeholder="your.email@example.com"
              />
              {errors.email && touched.email && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 animate-fade-in">
                  <FaExclamationCircle />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="modal-message" className="block text-gray-900 dark:text-white font-semibold mb-2 flex items-center gap-2">
                <FaComment className="text-primary dark:text-teal" />
                Message *
              </label>
              <textarea
                id="modal-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                rows="5"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.message && touched.message
                    ? 'border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900'
                    : 'border-gray-300 dark:border-gray-600 focus:border-primary dark:focus:border-teal focus:ring-2 focus:ring-primary/20 dark:focus:ring-teal/20'
                }`}
                placeholder="Tell us about your project or question..."
              ></textarea>
              {errors.message && touched.message && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1 animate-fade-in">
                  <FaExclamationCircle />
                  {errors.message}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {formData.message.length}/1000 characters
              </p>
            </div>

            {/* Status Message */}
            {status.message && (
              <div
                className={`p-4 rounded-xl flex items-center gap-3 animate-fade-in ${
                  status.type === 'success'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-2 border-green-300 dark:border-green-700'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-2 border-red-300 dark:border-red-700'
                }`}
              >
                {status.type === 'success' ? (
                  <FaCheckCircle className="text-xl flex-shrink-0" />
                ) : (
                  <FaExclamationCircle className="text-xl flex-shrink-0" />
                )}
                <p className="font-medium">{status.message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 dark:from-teal dark:to-primary text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  Send Message
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              We typically respond within 24 hours
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
